/**
 * RegistroRecursosRepo sobre Supabase. Espeja al de Dexie.
 */

import type { RegistroRecursos } from "@app-types/entities";
import type { RegistroRecursosRepo } from "@core/data/repos/RegistroRecursosRepo";
import { SupabaseBaseRepo } from "./SupabaseBaseRepo";

export class SupabaseRegistroRecursosRepo
  extends SupabaseBaseRepo<RegistroRecursos>
  implements RegistroRecursosRepo
{
  constructor(tenantId: string) {
    super("registroRecursos", tenantId);
  }

  async list(): Promise<RegistroRecursos[]> {
    const todos = await this.listAll();
    return todos.sort((a, b) => b.fecha.localeCompare(a.fecha));
  }

  async getDelDia(contratistaId: string, fecha: string): Promise<RegistroRecursos | null> {
    const todos = await this.listAll();
    return todos.find((r) => r.contratistaId === contratistaId && r.fecha === fecha) ?? null;
  }

  async listByContratista(contratistaId: string): Promise<RegistroRecursos[]> {
    const todos = await this.listAll();
    return todos
      .filter((r) => r.contratistaId === contratistaId)
      .sort((a, b) => b.fecha.localeCompare(a.fecha));
  }

  async get(id: string): Promise<RegistroRecursos | null> {
    return this.getById(id);
  }

  async create(
    data: Omit<RegistroRecursos, "id" | "createdAt" | "updatedAt" | "version">,
  ): Promise<string> {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    await this.createRaw({
      ...data,
      id,
      tenantId: this.tenantId,
      createdAt: now,
      updatedAt: now,
      version: 1,
    } as RegistroRecursos);
    return id;
  }

  async update(
    id: string,
    data: Partial<RegistroRecursos>,
    expectedVersion: number,
  ): Promise<void> {
    const actual = await this.get(id);
    if (!actual) throw new Error("Registro de recursos no encontrado");
    if (actual.version !== expectedVersion) {
      throw new Error(
        "Conflicto de versión: el registro fue modificado por otro usuario " +
          `(v${actual.version} vs v${expectedVersion}).`,
      );
    }
    await this.putRaw({
      ...actual,
      ...data,
      id: actual.id,
      tenantId: actual.tenantId,
      // La autoría de creación es inmutable, igual que en el resto del PMIS.
      createdAt: actual.createdAt,
      createdBy: actual.createdBy,
      updatedAt: new Date().toISOString(),
      version: actual.version + 1,
    });
  }

  async delete(id: string): Promise<void> {
    await this.deleteById(id);
  }
}
